import { useCallback, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Mover la bola de Gustavo a otro lado de la pantalla. (13/10/2026, pedido de Imperium:
 * tapaba botones de la esquina de abajo a la derecha)
 *
 * La posición vive en el MÓDULO, no en el componente: la bola se desmonta cada vez que se
 * abre el chat y tiene que volver a donde la dejaron. Al recargar la página vuelve a su
 * lugar de siempre, a pedido.
 *
 * Se guarda como distancia a la esquina MÁS CERCANA (izquierda/derecha y arriba/abajo):
 * así al achicar la ventana sigue pegada a su rincón, y la burbuja de sugerencias se abre
 * siempre hacia el centro de la pantalla en vez de salirse por el borde.
 */
export interface PosicionBola {
  lado: 'left' | 'right';
  h: number;
  vert: 'top' | 'bottom';
  v: number;
}

/** Cuánto hay que mover el puntero para que deje de ser un click. */
const UMBRAL_PX = 6;
const BORDE = 8;

let posicionGuardada: PosicionBola | null = null;

export function useBolaArrastrable() {
  const [pos, setPos] = useState<PosicionBola | null>(posicionGuardada);
  const [arrastrando, setArrastrando] = useState(false);
  const inicio = useRef<{ px: number; py: number; rect: DOMRect; movio: boolean } | null>(null);
  // El click que llega al soltar un arrastre no tiene que abrir el chat.
  const tragarClick = useRef(false);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    // ⛔ Sin capturar el puntero acá: con la captura puesta desde el primer momento, el
    // click iba al contenedor y no a la bola, y tocarla dejó de abrir el chat. Se captura
    // recién cuando el movimiento ya es un arrastre.
    inicio.current = { px: e.clientX, py: e.clientY, rect: e.currentTarget.getBoundingClientRect(), movio: false };
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const i = inicio.current;
    if (!i) return;
    // Soltó afuera de la bola antes de arrastrar: el pointerup no llegó acá.
    if (e.buttons === 0) { inicio.current = null; return; }
    const dx = e.clientX - i.px;
    const dy = e.clientY - i.py;
    if (!i.movio) {
      if (Math.hypot(dx, dy) < UMBRAL_PX) return;
      i.movio = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      setArrastrando(true);
    }
    const W = window.innerWidth, H = window.innerHeight;
    // Toda la bola adentro de la ventana.
    const left = Math.min(Math.max(i.rect.left + dx, BORDE), W - i.rect.width - BORDE);
    const top  = Math.min(Math.max(i.rect.top + dy, BORDE), H - i.rect.height - BORDE);
    const cx = left + i.rect.width / 2, cy = top + i.rect.height / 2;
    const nueva: PosicionBola = {
      lado: cx < W / 2 ? 'left' : 'right',
      h:    cx < W / 2 ? left : W - (left + i.rect.width),
      vert: cy < H / 2 ? 'top' : 'bottom',
      v:    cy < H / 2 ? top : H - (top + i.rect.height),
    };
    posicionGuardada = nueva;
    setPos(nueva);
  }, []);

  const soltar = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const i = inicio.current;
    inicio.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (i?.movio) { tragarClick.current = true; setArrastrando(false); }
  }, []);

  const onClickCapture = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!tragarClick.current) return;
    tragarClick.current = false;
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return {
    pos,
    arrastrando,
    manija: { onPointerDown, onPointerMove, onPointerUp: soltar, onPointerCancel: soltar, onClickCapture },
  };
}
